import axios from 'axios';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import i18n from './i18n';
import { hasValidSubscription } from './utils/subscription';
import { normalizeLanguage } from './utils/languages';

// Subscription store for tracking trial/subscription status
export const useSubscriptionStore = create((set, get) => ({
  subscriptionInfo: null,
  loading: false,
  error: null,

  fetchSubscriptionInfo: async () => {
    set({ loading: true, error: null });
    try {
      const response = await axios.get('/api/payments/subscription/info/');
      set({ subscriptionInfo: response.data, loading: false });
      return response.data;
    } catch (error) {
      set({ error: error.message, loading: false });
      return null;
    }
  },

  isTrial: () => {
    const { subscriptionInfo } = get();
    return subscriptionInfo?.is_trial === true;
  },

  hasActiveSubscription: () => {
    const { subscriptionInfo } = get();
    return subscriptionInfo?.has_subscription === true && !subscriptionInfo?.is_trial;
  },

  // Check if SMS feature is available (not available during trial)
  canUseSms: () => {
    const { subscriptionInfo } = get();
    if (!subscriptionInfo) return false;
    return subscriptionInfo.has_subscription && !subscriptionInfo.is_trial;
  },

  // Check if e-invoicing feature is available (not available during trial)
  canUseEinvoice: () => {
    const { subscriptionInfo } = get();
    if (!subscriptionInfo) return false;
    return subscriptionInfo.has_subscription && !subscriptionInfo.is_trial;
  },

  // OEM huolto-ohjelma: every lookup costs real money per vehicle type and trial
  // usage is deliberately unbilled, so trials cannot enable it. Mirrors the
  // backend guard in oem_service/permissions.py — an org without subscription
  // info loaded yet is not treated as a trial.
  canUseOemService: () => {
    const { subscriptionInfo } = get();
    if (!subscriptionInfo) return true;
    return !subscriptionInfo.is_trial;
  },

  clearSubscriptionInfo: () => {
    set({ subscriptionInfo: null, loading: false, error: null });
  },
}));

export const useUserStore = create((set, get) => ({
  user: null,
  error: null,
  loading: false,
  currentOrganization: null,

  fetchUser: async () => {
    set({ loading: true });
    try {
      const response = await axios.get(`/api/users/me/`);
      const user = response.data;
      set({ user, error: null });

      const organizations = user.organizations || [];

      if (organizations.length > 0) {
        // First check localStorage for saved organization
        const savedOrgId = localStorage.getItem('currentOrganizationId');
        let selectedOrg = null;

        if (savedOrgId) {
          selectedOrg = organizations.find(
            (org) => org.organization_id === parseInt(savedOrgId, 10)
          );
        }

        // No saved org (first login, cleared storage) or the saved one is gone: prefer an
        // organization the user can actually work in, else the first one.
        if (!selectedOrg) {
          selectedOrg = organizations.find(hasValidSubscription) || organizations[0];
          localStorage.setItem('currentOrganizationId', selectedOrg.organization_id);
        }

        set({ currentOrganization: selectedOrg });

        // Set UI language: user preference → org default → fi
        const lang = normalizeLanguage(user.preferred_language || selectedOrg.default_language);
        i18n.changeLanguage(lang);
        localStorage.setItem('userLanguage', lang);
      }
    } catch (error) {
      // Set user to null and error on authentication failure
      set({ user: null, error: error.message });

      // If it's an authentication error (401 or 403), return the status
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        set({ loading: false });
        throw new Error('UNAUTHENTICATED');
      }
    } finally {
      set({ loading: false });
    }
  },

  setCurrentOrganization: (organization) => {
    set({ currentOrganization: organization });
    if (organization) {
      localStorage.setItem('currentOrganizationId', organization.organization_id);
      // Update language on org switch: user preference → org default → fi
      const user = get().user;
      const lang = normalizeLanguage(user?.preferred_language || organization.default_language);
      i18n.changeLanguage(lang);
      localStorage.setItem('userLanguage', lang);
    } else {
      localStorage.removeItem('currentOrganizationId');
    }
  },

  // Switch to another organization the user belongs to and hard-navigate to the front page: a
  // full document load wipes all in-memory state, and landing on "/" avoids leaving the user on
  // a route (e.g. /maintenances/123) that may not exist in the new organization.
  // setCurrentOrganization has already written currentOrganizationId to localStorage, which the
  // fresh load reads to pick the new org. Used by the sidebar OrganizationSelector and by the
  // SubscriptionGate's "Switch organization" card.
  switchOrganization: (organization) => {
    get().setCurrentOrganization(organization);
    window.location.assign('/');
  },

  // Merge a few fields into currentOrganization (and the matching user.organizations entry)
  // without a /api/users/me/ round trip, which would flip `loading` and re-render the app shell.
  // Used after an action that changes org state the store caches (e.g. Google Calendar reconnect).
  patchCurrentOrganization: (patch) => {
    set((state) => {
      if (!state.currentOrganization) return {};
      const updated = { ...state.currentOrganization, ...patch };
      const organizations = (state.user?.organizations || []).map((org) =>
        org.organization_id === updated.organization_id ? { ...org, ...patch } : org
      );
      return {
        currentOrganization: updated,
        user: state.user ? { ...state.user, organizations } : state.user,
      };
    });
  },

  // Initialize from localStorage on app load
  initializeOrganization: () => {
    const savedOrgId = localStorage.getItem('currentOrganizationId');
    const user = get().user;

    if (savedOrgId && user?.organizations) {
      const org = user.organizations.find(
        (o) => o.organization_id === parseInt(savedOrgId, 10)
      );
      if (org) {
        set({ currentOrganization: org });
      }
    }
  },
}));

// Helper to get organization ID for API calls
export const getOrganizationId = () => {
  const state = useUserStore.getState();
  return state.currentOrganization?.organization_id || null;
};